import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { z } from 'zod';
import { StoreNames } from '../../../shared/store-names';
import { useDataTableSettings } from '../../../composables';
import { useUiStateAutoPersist, useUiStatesApi } from '../../ui-states';
import { Columns } from '../shared/location-details-columns';

const UI_STATES_KEY = StoreNames.ReplenishmentLocationDetailsPage;

export const useReplenishmentLocationDetailsPageStore = defineStore(
  StoreNames.ReplenishmentLocationDetailsPage,
  () => {
    const defaultWidth = 680;
    const width = ref<number>(defaultWidth);

    const {
      columnsVisibility,
      columnsOrder,
      visibleColumns,
      resetColumnsSettings,
      columnsSettingsTouched,
      userColumnsWidth,
      applyPersistedState,
      handleColumnResize,
      pinnedColumns,
      columnsWidth,
    } = useDataTableSettings({
      defaultColumnsVisibility: {
        [Columns.SkuChart]: true,
        [Columns.Size]: true,
        [Columns.SkuName]: false,
        [Columns.SkuExternalId]: false,
        [Columns.Replenishment]: true,
        [Columns.Sold]: true,
        [Columns.SalesRate]: false,
        [Columns.Stock]: true,
        [Columns.OptimalStock]: true,
        [Columns.WarehouseInventory]: false,
        [Columns.WhNames]: false,
        [Columns.NextReplenishment]: false,
        [Columns.Coverage]: false,
        [Columns.ExpectedCoverage]: false,
        [Columns.Constraints]: true,
        [Columns.MasterSkuName]: true,
        [Columns.MasterSkuExternalId]: false,
      },

      defaultColumnsWidth: {
        [Columns.Size]: 96,
        [Columns.SkuName]: 256,
        [Columns.SkuExternalId]: 192,
        [Columns.Replenishment]: 164,
        [Columns.Sold]: 96,
        [Columns.SalesRate]: 164,
        [Columns.Stock]: 96,
        [Columns.OptimalStock]: 132,
        [Columns.WarehouseInventory]: 132,
        [Columns.WhNames]: 96,
        [Columns.NextReplenishment]: 192,
        [Columns.Coverage]: 96,
        [Columns.ExpectedCoverage]: 164,
        [Columns.Constraints]: 192,
        [Columns.MasterSkuName]: 256,
        [Columns.MasterSkuExternalId]: 256,
      },
      defaultColumnsOrder: [
        Columns.Size,
        Columns.SkuName,
        Columns.SkuExternalId,
        Columns.Replenishment,
        Columns.Sold,
        Columns.SalesRate,
        Columns.Stock,
        Columns.OptimalStock,
        Columns.WarehouseInventory,
        Columns.WhNames,
        Columns.NextReplenishment,
        Columns.Coverage,
        Columns.ExpectedCoverage,
        Columns.Constraints,
        Columns.MasterSkuName,
        Columns.MasterSkuExternalId,
      ],
      defaultPinnedColumns: [],
    });

    function reset() {
      resetColumnsSettings();
    }

    const api = useUiStatesApi();

    const fetching = ref(false);
    const fetched = ref(false);

    let fetchingPromise: Promise<void> | null = null;

    async function fetch(force = false) {
      if (fetched.value && !force) {
        return fetchingPromise ?? Promise.resolve();
      }

      if (!fetchingPromise) {
        fetchingPromise = (async () => {
          fetching.value = true;

          const persistedState = await api
            .getUiStates({ key: UI_STATES_KEY })
            .then(({ data }) => data.data[0]);

          reset();

          // Apply persisted state
          if (persistedState) {
            applyPersistedState(persistedState.value);

            if (persistedState.value.width) {
              try {
                width.value = z.number().parse(persistedState.value.width);
              } catch (error) {
                // do nothing
              }
            }
          }

          fetching.value = false;
          fetched.value = true;
          fetchingPromise = null;
        })();
      }

      return fetchingPromise;
    }

    const persistingValue = computed(() => ({
      columnsVisibility: { ...columnsVisibility.value },
      columnsWidth: { ...userColumnsWidth.value },
      columnsOrder: [...columnsOrder.value],
      pinnedColumnsOrder: [...pinnedColumns.value],
      width: width.value,
    }));

    useUiStateAutoPersist({
      enabled: fetched,
      value: persistingValue,
      key: UI_STATES_KEY,
    });

    return {
      fetching,
      fetched,
      fetch,
      reset,
      resetColumnsSettings,
      columnsVisibility,
      columnsWidth,
      visibleColumns,
      columnsOrder,
      pinnedColumns,
      columnsSettingsTouched,
      handleColumnResize,
      width,
    };
  },
);
